/**
 * User Management Page - Admin Only
 * Sprint 1 / Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../context/AuthContext';
import { usersAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, 
  Plus, 
  Shield, 
  Archive,
  RefreshCw,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

function UserManagement() {
  const navigate = useNavigate();
  const { user, logout, canManageUsers, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'Caregiver'
  });
  const [newRole, setNewRole] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.list({ include_archived: includeArchived });
      setUsers(response.data.users);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Wait for auth to complete before checking permissions
    if (authLoading) return;
    
    if (canManageUsers) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [canManageUsers, authLoading, includeArchived]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsers();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await usersAPI.create(newUser);
      toast.success('User created successfully');
      setCreateModalOpen(false);
      setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'Caregiver' });
      fetchUsers();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : 'Failed to create user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!reason || reason.length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await usersAPI.updateRole(selectedUser.id, newRole, reason);
      toast.success('Role updated successfully');
      setRoleModalOpen(false);
      setSelectedUser(null);
      setNewRole('');
      setReason('');
      fetchUsers();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : 'Failed to update role';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveUser = async () => {
    if (!reason || reason.length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await usersAPI.archive(selectedUser.id, reason);
      toast.success('User archived successfully');
      setArchiveModalOpen(false);
      setSelectedUser(null);
      setReason('');
      fetchUsers();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : 'Failed to archive user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const openRoleModal = (u) => {
    setSelectedUser(u);
    setNewRole(u.role);
    setReason('');
    setRoleModalOpen(true);
  };

  const openArchiveModal = (u) => {
    setSelectedUser(u);
    setReason('');
    setArchiveModalOpen(true);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      Admin: 'bg-red-100 text-red-700 border-red-200',
      ComplianceManager: 'bg-purple-100 text-purple-700 border-purple-200',
      Scheduler: 'bg-blue-100 text-blue-700 border-blue-200',
      RNSupervisor: 'bg-green-100 text-green-700 border-green-200',
      Caregiver: 'bg-amber-100 text-amber-700 border-amber-200',
      Billing: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    return colors[role] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  // Wait for auth loading to complete
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <Layout title="User Management">
        <div className="flex items-center justify-center py-12">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-600 mb-4">
                This page is only accessible to Administrators.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <Layout title="User Management">
      <div data-testid="user-management-page">
        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button className="bg-avida-blue hover:bg-avida-blue/90" onClick={() => setCreateModalOpen(true)} data-testid="create-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Create User
            </Button>
            <label className="flex items-center space-x-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span>Show archived</span>
            </label>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'h-4 w-4 mr-1 animate-spin' : 'h-4 w-4 mr-1'} />
            Refresh
          </Button>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Users ({users.length})</CardTitle>
            <CardDescription>All system users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-900">
                          {u.first_name} {u.last_name}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{u.email}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={getRoleBadgeColor(u.role)}>
                          {u.role}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {u.is_archived ? (
                          <Badge variant="outline" className="bg-slate-100 text-slate-600">Archived</Badge>
                        ) : u.is_active ? (
                          <Badge variant="outline" className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700">Inactive</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {!u.is_archived && u.id !== user.id && (
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRoleModal(u)}
                              data-testid={`change-role-${u.id}`}
                            >
                              <Shield className="h-3 w-3 mr-1" />
                              Role
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openArchiveModal(u)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`archive-user-${u.id}`}
                            >
                              <Archive className="h-3 w-3 mr-1" />
                              Archive
                            </Button>
                          </div>
                        )}
                        {u.id === user.id && (
                          <span className="text-xs text-slate-400">Current user</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user to the system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                    required
                    data-testid="create-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                    required
                    data-testid="create-last-name"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  data-testid="create-email"
                />
              </div>
              <div>
                <Label>Temporary Password</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  minLength={8}
                  data-testid="create-password"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                  <SelectTrigger data-testid="create-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ROLES).map((role) => (
                      <SelectItem key={role} value={role}>{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="create-submit">
                {submitting ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Role Modal */}
      <Dialog open={roleModalOpen} onOpenChange={setRoleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update role for {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ROLES).map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason for Change (required, min 10 characters)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why this role change is needed..."
                rows={3}
                data-testid="role-reason"
              />
              <p className="text-xs text-slate-500 mt-1">{reason.length}/10 characters minimum</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRoleModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={submitting || reason.length < 10} data-testid="role-submit">
              {submitting ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive User Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive User</DialogTitle>
            <DialogDescription>
              Archive {selectedUser?.first_name} {selectedUser?.last_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Reason for Archiving (required, min 10 characters)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why this user is being archived..."
                rows={3}
                data-testid="archive-reason"
              />
              <p className="text-xs text-slate-500 mt-1">{reason.length}/10 characters minimum</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleArchiveUser} 
              disabled={submitting || reason.length < 10}
              variant="destructive"
              data-testid="archive-submit"
            >
              {submitting ? 'Archiving...' : 'Archive User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}

export default UserManagement;
